import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import { CommandLoading } from 'cmdk';
import { ComponentType, Fragment, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import List from 'react-virtualized/dist/es/List';
import Spinner from '../spinner/Spinner';
import { Alert } from '../ui/alert';
import UIError from '@/errors/UIError';
import { Badge } from '../ui/badge';
import { X } from 'lucide-react';

export type AutoCompleteItem = {
	value: string;
	label: string;
};

export type AutoCompleteProps = {
	selectedItems: AutoCompleteItem[];
	items: AutoCompleteItem[];
	loading?: boolean;
	isMultiSelect?: boolean;
	onSearch?: (search: string) => void;
	onRemoveItem?: (item: AutoCompleteItem) => void;
	onSelect?: (selected: AutoCompleteItem) => void;
	onClear?: () => void;
	defaultSearchValue?: string;
	placeholder?: string;
	error?: Error | null;
	closeOnSelect?: boolean;
	TriggerWrapper?: ComponentType<{ children: ReactNode }>;
	isDisabled?: boolean;
};

export default function AutoComplete({
	selectedItems,
	items = [],
	loading,
	isMultiSelect = false,
	onSearch,
	onSelect,
	onRemoveItem,
	onClear,
	defaultSearchValue = '',
	placeholder = 'Select an item',
	error,
	closeOnSelect = false,
	TriggerWrapper = Fragment,
	isDisabled = false
}: AutoCompleteProps) {
	const [open, setOpen] = useState(false);
	const [searchValue, setSearchValue] = useState(defaultSearchValue);

	const buttonRef = useRef<HTMLButtonElement>(null);
	const [buttonWidth, setButtonWidth] = useState(0);

	const filteredItems = items.filter(item => item.label.toLowerCase().includes(searchValue?.toLowerCase()));

	useLayoutEffect(() => {
		const handleResize = () => {
			const curentWidth = buttonRef.current?.offsetWidth;
			if (curentWidth && curentWidth !== buttonWidth) {
				return setButtonWidth(curentWidth);
			}
		};

		window.addEventListener('resize', handleResize);
		handleResize();
		return () => window.removeEventListener('resize', handleResize);
	}, [buttonWidth]);

	const displaySelectedItemsBadges = () => {
		return (
			<div className="flex w-full max-w-full gap-2 flex-wrap items-center">
				{selectedItems.map(item => (
					<Badge key={item?.value} variant="default">
						{item?.label}
						<div
							role="button"
							title="Remove option"
							tabIndex={0}
							onClick={e => {
								onRemoveItem?.(item);
								e.stopPropagation();
							}}
						>
							<X className="h-4 w-4 shrink-0" />
						</div>
					</Badge>
				))}
			</div>
		);
	};

	const displaySelectedOptions = () => {
		if (selectedItems.length === 0) {
			return <span className="text-slate-500 dark:text-slate-400 text-sm font-normal">{placeholder}</span>;
		}

		if (selectedItems.length > 0 && isMultiSelect) {
			return displaySelectedItemsBadges();
		}
		if (selectedItems.length > 0 && !isMultiSelect) {
			return selectedItems[0].label;
		}
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<TriggerWrapper>
				<PopoverTrigger asChild>
					<Button
						ref={buttonRef}
						variant="outline"
						role="combobox"
						aria-label={selectedItems[0]?.label ?? placeholder}
						aria-expanded={open}
						disabled={loading || isDisabled}
						className="w-full flex flex-row justify-between items-center h-[auto] hover:bg-slate-100 dark:hover:bg-slate-200/50"
					>
						{displaySelectedOptions()}
						<div className="flex items-center">
							{selectedItems.length > 0 && (
								<div
									className="flex items-start p-0"
									role="button"
									tabIndex={0}
									title="Clear all"
									onClick={e => {
										onClear?.();
										e.stopPropagation();
									}}
								>
									<X className="h-4 w-4 shrink-0 opacity-50" />
								</div>
							)}
							{!loading && (
								<ChevronDownIcon
									className={cn('ml-2 h-4 w-4 shrink-0 opacity-50', {
										'rotate-180': open
									})}
								/>
							)}

							{loading && <Spinner />}
						</div>
					</Button>
				</PopoverTrigger>
			</TriggerWrapper>
			<PopoverContent className="w-full p-0">
				<Command shouldFilter={false}>
					<CommandInput
						placeholder="Search items..."
						value={searchValue}
						onValueChange={search => {
							setSearchValue(search);
							onSearch?.(search);
						}}
					/>

					{error && (
						<>
							<Alert className="bg-red-300 w-[90%] mx-auto my-5 flex flex-col">
								<span className="font-semibold">Error:</span> {error?.message}
							</Alert>
							<CommandList />
						</>
					)}

					{loading && (
						<>
							<CommandLoading className="p-10">
								<Spinner className="mx-auto" />
							</CommandLoading>
							<CommandList />
						</>
					)}
					{!loading && !error && (
						<>
							<CommandEmpty>No items found.</CommandEmpty>
							<CommandList>
								<List
									height={300}
									width={buttonWidth}
									rowCount={items.length}
									rowHeight={30}
									rowRenderer={({ index, style }) => {
										const item = filteredItems[index];
										if (!item) {
											return null;
										}
										return (
											<CommandItem
												key={item.value}
												value={item.value}
												style={style}
												onSelect={currentValue => {
													const selectedItem = items.find(el => el.value === currentValue);

													if (!selectedItem) {
														throw new UIError(
															`Selected item id: ${currentValue} not found in the list of items`
														);
													}

													onSelect?.(selectedItem);
													if (closeOnSelect) {
														setOpen(false);
													}
												}}
											>
												<CheckIcon
													className={cn(
														'mr-2 h-4 w-4',
														selectedItems.find(el => el?.value === item.value)
															? 'opacity-100'
															: 'opacity-0'
													)}
												/>
												{item.label}
											</CommandItem>
										);
									}}
								/>
							</CommandList>
						</>
					)}
				</Command>
			</PopoverContent>
		</Popover>
	);
}
